/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { SessionIndexed } from "../models/patient.model";
import * as Sentry from "@sentry/angular-ivy";
import {
  Image,
  ImageResponse,
  ImageOrderMode,
  RawImageOrderModes,
  ImageLike,
  ImageType,
} from "../models/image.model";
import {
  keepUnique,
  isNullable,
  stringifyUnknownError,
  toImageBase64,
} from "@kells/utils/js";
import { isFinding } from "../models/finding.model";
import { ClassificationService } from "./classification.service";
import { ImageFilters } from "../models/image-template.model";
import { flatten } from "lodash-es";
import {
  forkJoin,
  from,
  Observable,
  of,
  throwError,
  timer,
} from "rxjs";
import {
  catchError,
  concatMap,
  finalize,
  map,
  mergeMap,
  retryWhen,
  switchMap,
} from "rxjs/operators";
import {
  CLINIC_ONE_API_BASE_URL,
  CLINIC_ONE_DIRECT_S3_UPLOAD_ENABLED,
  CLINIC_ONE_API_VER,
} from "@kells/clinic-one/environments";
import { TemplateType } from "../models";
import { FindingAdapter } from "../adapters/finding.adapter";
import { FindingStatus } from "@kells/interfaces/finding";
import {
  SessionCreationSuccess,
  SessionEventCode,
} from "../models/session.model";
import { SubmitReviewJobSurveyRarams } from "libs/clinic-one/data-access/review-jobs/src/lib/models/review-job.model";
import { ReviewJobService } from "@kells/clinic-one/data-access/review-jobs";
import imageCompression from "browser-image-compression";

interface ImageUploadSuccessResp {
  message: string;
  data: {
    image_id: string;
  };
}

interface UploadInitResponse {
  message: string;
  data: {
    upload_session_id: string;
    strategy: "single_part" | "multipart";
    object_key: string;
    expires_at: string;
    url_expires_at?: string;
    single_part?: {
      method: "PUT";
      url: string;
      required_headers: Record<string, string>;
      expires_at?: string;
    };
    multipart?: {
      upload_id: string;
      part_size_bytes: number;
      total_parts: number;
      signed_parts: { part_number: number; url: string; expires_at?: string }[];
      already_completed_parts?: number[];
    };
  };
}

interface UploadPartSignResponse {
  message: string;
  data: {
    upload_session_id: string;
    parts: { part_number: number; url: string; expires_at?: string }[];
  };
}

/** Persisted client-side state that lets an upload resume after a reload. */
interface PersistedUploadIntent {
  init_idempotency_key: string;
  complete_idempotency_key: string;
  client_upload_id: string;
  correlation_id: string;
  upload_session_id?: string;
}

/**
 * @category Service
 */
@Injectable({
  providedIn: "root",
})
export class ImageService {
  private readonly BASE_URL = `${this.baseUrl}/${this.apiVersion}/images`;
  private readonly UPLOADS_BASE_URL = `${this.baseUrl}/api/v2/uploads`;
  private readonly SESSIONS_BASE_URL = `${this.baseUrl}/${this.apiVersion}/sessions`;
  private readonly UPLOAD_RETRIES = 2;
  private readonly UPLOAD_RETRY_DELAY_MS = 1200;
  private readonly DIRECT_UPLOAD_MAX_RETRIES = 3;
  private readonly DIRECT_UPLOAD_MAX_DELAY_MS = 30000;
  // Keep concurrency small: too much parallelism saturates mobile uplink and
  // starves other network work (API calls, telemetry). 4 is the sweet spot
  // observed across AWS SDK defaults.
  private readonly MULTIPART_CONCURRENCY = 4;
  private readonly PERSIST_STORAGE_PREFIX = "kells.upload-intent.";
  private readonly PERSIST_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

  constructor(
    @Inject(CLINIC_ONE_API_BASE_URL) private baseUrl: string,
    @Inject(CLINIC_ONE_API_VER) private apiVersion: string,
    @Inject(CLINIC_ONE_DIRECT_S3_UPLOAD_ENABLED)
    private readonly directS3UploadEnabled: boolean,
    private http: HttpClient,
    private readonly reviewJobService: ReviewJobService
  ) { }

  private apiUrl = (imageId: string) => `${this.BASE_URL}/${imageId}`;

  private async firstFrom<T>(observable: Observable<T>): Promise<T> {
    const value = await observable.toPromise();
    if (value === undefined) {
      throw new Error("Observable completed without emitting a value");
    }
    return value;
  }

  private getHttpErrorDetails(error: unknown): {
    isHttpError: boolean;
    status: number;
    statusText: string;
    url: string;
    responseMessage: string;
  } {
    if (!(error instanceof HttpErrorResponse)) {
      return {
        isHttpError: false,
        status: -1,
        statusText: "",
        url: "",
        responseMessage: "",
      };
    }

    let responseMessage = "";
    const errBody = error.error;
    if (typeof errBody === "string") {
      responseMessage = errBody;
    } else if (errBody && typeof errBody === "object") {
      const bodyMessage = (errBody as { message?: string; error?: string }).message;
      const bodyError = (errBody as { message?: string; error?: string }).error;
      responseMessage = bodyMessage || bodyError || "";
    }

    return {
      isHttpError: true,
      status: error.status,
      statusText: error.statusText || "",
      url: error.url || "",
      responseMessage,
    };
  }

  private isRetryableUploadError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;

    return (
      error.status === 0 ||
      error.status === 408 ||
      error.status === 425 ||
      error.status === 429 ||
      error.status >= 500
    );
  }

  /**
   * Only fall back to the legacy base64 path on errors that could reasonably
   * succeed via a different transport: network failures, timeouts, or 5xx.
   * Validation errors (400/403/404/409/413) are *deterministic*; retrying
   * them through the legacy path will just fail again and waste bandwidth.
   */
  private shouldFallbackFromDirectError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return (
        error.status === 0 ||
        error.status === 408 ||
        error.status === 425 ||
        error.status === 429 ||
        error.status >= 500
      );
    }
    // Our XHR/network layer throws plain Errors carrying a status property for
    // non-HttpClient errors. Treat non-numeric / unknown as fallback-eligible.
    const maybeStatus = (error as { status?: number } | null)?.status;
    if (typeof maybeStatus === "number") {
      return (
        maybeStatus === 0 ||
        maybeStatus === 408 ||
        maybeStatus === 425 ||
        maybeStatus === 429 ||
        maybeStatus >= 500
      );
    }
    // Aborts are explicitly user-initiated; never fall back.
    if ((error as { name?: string } | null)?.name === "AbortError") {
      return false;
    }
    return true;
  }

  private createCorrelationId(): string {
    try {
      return (globalThis as any).crypto.randomUUID();
    } catch {
      return `${Date.now()}-${Math.random()}`;
    }
  }

  private isRetryableFetchStatus(status: number): boolean {
    return status === 408 || status === 425 || status === 429 || status >= 500;
  }

  private async waitForBackoff(attempt: number): Promise<void> {
    const baseDelay = Math.min(
      this.DIRECT_UPLOAD_MAX_DELAY_MS,
      this.UPLOAD_RETRY_DELAY_MS * 2 ** attempt
    );
    const jitter = Math.floor(Math.random() * 250);
    await new Promise((resolve) => setTimeout(resolve, baseDelay + jitter));
  }

  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    context: string,
    signal?: AbortSignal
  ): Promise<T> {
    let attempt = 0;
    while (true) {
      if (signal?.aborted) {
        const abortErr = new Error("Upload aborted by caller");
        abortErr.name = "AbortError";
        throw abortErr;
      }
      try {
        return await operation();
      } catch (error) {
        if ((error as { name?: string } | null)?.name === "AbortError") {
          throw error;
        }
        const shouldRetry =
          attempt < this.DIRECT_UPLOAD_MAX_RETRIES &&
          (this.isRetryableUploadError(error) ||
            (error instanceof Error &&
              (error.message.includes("fetch failed") ||
                error.message.includes("network error"))));
        if (!shouldRetry) {
          throw error;
        }
        attempt += 1;
        Sentry.captureMessage(
          `[Photo Scan] Retrying direct upload operation. Context: ${context}. Attempt: ${attempt}/${this.DIRECT_UPLOAD_MAX_RETRIES}`
        );
        await this.waitForBackoff(attempt);
      }
    }
  }

  /**
   * PUT a blob to an S3 pre-signed URL via XMLHttpRequest so we get:
   *   1. real upload progress events (fetch cannot emit upload progress),
   *   2. AbortController-based cancellation.
   * Returns the S3 ETag header value from the response.
   */
  private putToSignedUrl(
    signedUrl: string,
    payload: Blob,
    requiredHeaders: Record<string, string>,
    context: string,
    signal?: AbortSignal,
    onProgress?: (loadedBytes: number, totalBytes: number) => void
  ): Promise<string> {
    return this.executeWithRetry(
      () =>
        new Promise<string>((resolve, reject) => {
          if (signal?.aborted) {
            const abortErr = new Error("Upload aborted by caller");
            abortErr.name = "AbortError";
            reject(abortErr);
            return;
          }
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", signedUrl, true);
          for (const [name, value] of Object.entries(requiredHeaders || {})) {
            xhr.setRequestHeader(name, value);
          }
          xhr.upload.onprogress = (event) => {
            if (onProgress && event.lengthComputable) {
              onProgress(event.loaded, event.total);
            }
          };
          const onAbort = () => {
            try {
              xhr.abort();
            } catch {
              // intentional: abort is best-effort
            }
          };
          signal?.addEventListener("abort", onAbort);
          xhr.onload = () => {
            signal?.removeEventListener("abort", onAbort);
            const status = xhr.status;
            if (status >= 200 && status < 300) {
              resolve(xhr.getResponseHeader("etag") || "");
              return;
            }
            if (this.isRetryableFetchStatus(status)) {
              reject(new Error(`fetch failed (status ${status})`));
              return;
            }
            const err = new Error(`S3 upload failed with status ${status}`);
            (err as { status?: number }).status = status;
            reject(err);
          };
          xhr.onerror = (_event: ProgressEvent) => {
            signal?.removeEventListener("abort", onAbort);
            reject(new Error(`S3 XHR network error: status=${xhr.status}`));
          };
          xhr.onabort = () => {
            signal?.removeEventListener("abort", onAbort);
            const abortErr = new Error("Upload aborted by caller");
            abortErr.name = "AbortError";
            reject(abortErr);
          };
          xhr.send(payload);
        }),
      context,
      signal
    );
  }

  private chunkFile(file: File, partSize: number): Blob[] {
    const parts: Blob[] = [];
    let offset = 0;
    while (offset < file.size) {
      const nextOffset = Math.min(offset + partSize, file.size);
      parts.push(file.slice(offset, nextOffset));
      offset = nextOffset;
    }
    return parts;
  }

  /** Hash key used to persist per-file upload intent in sessionStorage. */
  private buildUploadIntentKey(patientId: string, file: File): string {
    return (
      `${this.PERSIST_STORAGE_PREFIX}${patientId}:${file.name}:${file.size}:` +
      `${file.lastModified}`
    );
  }

  private getStorage(): Storage | null {
    try {
      if (typeof sessionStorage !== "undefined") return sessionStorage;
    } catch {
      // Access can throw in sandboxed contexts (private mode, etc.)
    }
    return null;
  }

  private loadUploadIntent(
    patientId: string,
    file: File
  ): PersistedUploadIntent | null {
    const storage = this.getStorage();
    if (!storage) return null;
    const key = this.buildUploadIntentKey(patientId, file);
    try {
      const raw = storage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        stored_at: number;
        intent: PersistedUploadIntent;
      };
      if (
        !parsed ||
        !parsed.intent ||
        Date.now() - parsed.stored_at > this.PERSIST_STORAGE_TTL_MS
      ) {
        storage.removeItem(key);
        return null;
      }
      return parsed.intent;
    } catch {
      return null;
    }
  }

  private saveUploadIntent(
    patientId: string,
    file: File,
    intent: PersistedUploadIntent
  ): void {
    const storage = this.getStorage();
    if (!storage) return;
    const key = this.buildUploadIntentKey(patientId, file);
    try {
      storage.setItem(
        key,
        JSON.stringify({ stored_at: Date.now(), intent })
      );
    } catch {
      // Storage quota / unavailable — intent-persistence is best-effort.
    }
  }

  private clearUploadIntent(patientId: string, file: File): void {
    const storage = this.getStorage();
    if (!storage) return;
    try {
      storage.removeItem(this.buildUploadIntentKey(patientId, file));
    } catch {
      // best-effort
    }
  }

  /**
   * Run async tasks with bounded concurrency. We can't use Promise.all directly
   * because it would launch all N part uploads simultaneously and saturate the
   * uplink. This keeps at most `limit` promises in flight at a time.
   */
  private async runWithConcurrency<T>(
    tasks: (() => Promise<T>)[],
    limit: number,
    signal?: AbortSignal
  ): Promise<T[]> {
    const results: T[] = new Array(tasks.length);
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < tasks.length) {
        if (signal?.aborted) {
          const abortErr = new Error("Upload aborted by caller");
          abortErr.name = "AbortError";
          throw abortErr;
        }
        const index = cursor;
        cursor += 1;
        results[index] = await tasks[index]();
      }
    };
    const workers = Array.from(
      { length: Math.min(limit, tasks.length) },
      () => worker()
    );
    await Promise.all(workers);
    return results;
  }

  private async uploadViaLegacyBase64(
    patientId: string,
    image: File,
    compressedImage: File,
    format: string,
    isPhotoScan: boolean,
    session_id?: string,
    headers?: Record<string, string>
  ): Promise<string> {
    const compressedType = compressedImage.type || image.type;
    const compressedFormat = compressedType.split("/")[1] || format;
    const base64String = await this.firstFrom(
      from(toImageBase64(compressedImage))
    );
    Sentry.captureMessage(
      `[Photo Scan] Uploading base64 fallback. File: ${compressedImage.name}. Size: ${compressedImage.size}`
    );
    const response = await this.firstFrom(
      this.http
        .post<ImageUploadSuccessResp>(
          this.BASE_URL,
          {
            patient_id: patientId,
            extension: compressedFormat,
            image_data_b64_string: base64String,
            is_photo_scan: isPhotoScan,
            session_id,
          },
          { headers }
        )
        .pipe(
          retryWhen((errors) =>
            errors.pipe(
              mergeMap((error, retryIndex: number) => {
                const shouldRetry =
                  retryIndex < this.UPLOAD_RETRIES &&
                  this.isRetryableUploadError(error);
                if (!shouldRetry) {
                  return throwError(error);
                }
                return timer(this.UPLOAD_RETRY_DELAY_MS * (retryIndex + 1));
              })
            )
          )
        )
    );
    return response.data.image_id;
  }

  private isDragging: boolean;

  public get dragInProgress(): boolean {
    return this.isDragging;
  }

  public set dragInProgress(isDragging: boolean) {
    this.isDragging = isDragging;
  }

  /**
   * Gets information about an image.
   *
   * @param imageId the ID of an image.
   * @returns an [[`Image`]] object containing information about the image.
   * @throws when the provided ID does not exist in the database.
   */
  getImage(imageId: string): Observable<Image> {
    return this.http
      .get<{ data: ImageResponse }>(this.apiUrl(imageId))
      .pipe(map((res) => ImageService.parseImageResponse(res.data)));
  }

  updateImage(imageId: string, payload: Record<string, any>) {
    return this.http.patch(this.apiUrl(imageId), { update_data: payload });
  }

  update(imageId: string, payload: Record<string, any>) {
    return this.http.patch(this.apiUrl(imageId), payload);
  }

  deleteImage(imageId: string) {
    return this.http.delete(this.apiUrl(imageId));
  }

  /**
   * Update the image order in a session, given the updated image ordering
   * and the order mode (whether the order change is requested by a user.)
   */
  updateSessionImageOrders(
    orderMode: ImageOrderMode.Predicted | ImageOrderMode.User,
    orderChanges: { imageId: string; orderId: number }[]
  ) {
    const updatedOrderPayload = orderChanges.map((change) => ({
      img_id: change.imageId,
      order_id: change.orderId,
    }));

    return this.http.patch(`${this.BASE_URL}/orders`, {
      order_mode: orderMode,
      orders: updatedOrderPayload,
    });
  }

  /**
   * Performs image rotation.
   *
   * @param imageId ID of the image to be rotated.
   *
   * @param orientation which direction to rotate 90 degrees to: 'left' or 'right'.
   *
   * @returns the rotated image instance. Rotation changes the coordinates of
   *   all the findings on this image. The updated image instance, returned
   *   here, is helpful for updating the application's state.
   */
  rotate(imageId: string, orientation: "left" | "right"): Observable<Image> {
    interface RotateImageResponse {
      msg: string;
      data: ImageResponse;
    }

    return this.http
      .post<RotateImageResponse>(`${this.apiUrl(imageId)}/rotate`, {
        orientation: orientation === "right" ? "clockwise" : "counterclockwise",
      })
      .pipe(
        map((response) => response.data),
        map(ImageService.parseImageResponse)
      );
  }

  /**
   * Update the template view of session
   * @param templateType is a type of template chosen by user
   * @param entries is an array of objects containing image_id and template_id
   */
  updateImageTemplate(
    templateType: TemplateType,
    entries: { imageId: string; templateId?: number }[]
  ): Observable<any> {
    return forkJoin(
      entries.map((entry) =>
        this.http.patch(this.apiUrl(entry.imageId), {
          template_id: entry.templateId ?? null,
          template_type: templateType,
        })
      )
    );
  }

  /**
   * Create a patient session by supplying the images for that session.
   *
   * @param patientId the patient to create a new session for.
   * @param image one or more files that represent images.
   *
   * @returns an observable that emits an array of image ID strings.
   */
  createPatientSession(
    patientId: string,
    image: File | File[],
    isPhotoScan = false,
    survey?: SubmitReviewJobSurveyRarams,
    options?: { signal?: AbortSignal }
  ): Observable<string[]> {
    const imageFiles: File[] = Array.isArray(image) ? image : [image];
    Sentry.captureMessage("[Photo Scan] Creating patient session");

    const sessionParams = {
      patient_id: patientId,
      ...(isPhotoScan ? { event_code: SessionEventCode.PhotoScan } : {}),
    };

    return this.http
      .post<SessionCreationSuccess>(this.SESSIONS_BASE_URL, sessionParams)
      .pipe(
        switchMap((response: SessionCreationSuccess) => {
          const sessionId = response.data.session_id;
          Sentry.captureMessage(`[Photo Scan] Session created: ${sessionId}`);

          const uploadImages = imageFiles.map((img) =>
            this.uploadImageForPatient(
              patientId,
              img,
              isPhotoScan,
              sessionId,
              undefined,
              options
            )
          );
          let tasks: Observable<string | void>[] = [...uploadImages];

          if (survey) {
            const submitSurvey = this.reviewJobService
              .submitReviewJobSurveyAnswers(sessionId, survey)
              .pipe(
                catchError((e) => {
                  Sentry.captureException(e);
                  Sentry.captureMessage(
                    `[Photo Scan] Failed to submit survey: ${e.message}`
                  );
                  return of("");
                })
              );
            tasks = [submitSurvey, ...tasks];
          }

          return forkJoin(tasks).pipe(
            // Return only the results of uploadImageForPatient calls.
            // slice(1) is only correct when a survey was prepended as the
            // first task; without a survey, all results are image IDs.
            map((results) => (survey ? results.slice(1) : results) as string[]),
            finalize(() => {
              Sentry.captureMessage("[Photo Scan] Marking session as ready");
              this.markSessionReady(sessionId).subscribe();
            })
          );
        }),
        catchError((e) => {
          Sentry.captureException(e);
          Sentry.captureMessage(
            `[Photo Scan] Failed to create session: ${e.message}`
          );
          throw new Error("Failed to create session");
        })
      );
  }

  public markSessionReady(sessionId: string, headers?: Record<string, string>) {
    return this.http.put(
      `${this.SESSIONS_BASE_URL}/${sessionId}/ready`,
      {},
      { headers }
    );
  }

  /**
   * Execute the direct-to-S3 upload flow. Supports:
   *   - single-part and multipart strategies chosen by the backend,
   *   - bounded-concurrency multipart part uploads (MULTIPART_CONCURRENCY),
   *   - real upload progress via XHR,
   *   - AbortSignal-based cancellation that also releases the server session,
   *   - stable idempotency keys across retries / page reloads so retries of
   *     `/init` and `/complete` do not create duplicate rows server-side.
   *
   * The per-part `/parts/ack` endpoint is intentionally NOT called in the
   * happy path: we pass the full parts list in `/complete`, which the backend
   * already supports. Skipping N ack round-trips is the single biggest wall-
   * clock win for large multipart uploads.
   */
  private async uploadImageViaDirectS3(
    patientId: string,
    image: File,
    isPhotoScan: boolean,
    session_id?: string,
    headers?: Record<string, string>,
    options?: {
      signal?: AbortSignal;
      onProgress?: (loadedBytes: number, totalBytes: number) => void;
    }
  ): Promise<string> {
    const signal = options?.signal;
    // Reuse persisted idempotency keys if we're resuming the same file so
    // repeated /init and /complete calls hit the existing server-side session
    // instead of returning IDEMPOTENCY_CONFLICT.
    const persistedIntent = this.loadUploadIntent(patientId, image);
    const correlationId =
      persistedIntent?.correlation_id ?? this.createCorrelationId();
    const initIdempotencyKey =
      persistedIntent?.init_idempotency_key ?? this.createCorrelationId();
    const completeIdempotencyKey =
      persistedIntent?.complete_idempotency_key ?? this.createCorrelationId();
    const clientUploadId =
      persistedIntent?.client_upload_id ?? this.createCorrelationId();
    this.saveUploadIntent(patientId, image, {
      init_idempotency_key: initIdempotencyKey,
      complete_idempotency_key: completeIdempotencyKey,
      client_upload_id: clientUploadId,
      correlation_id: correlationId,
      upload_session_id: persistedIntent?.upload_session_id,
    });

    const initHeaders = {
      ...(headers || {}),
      "Idempotency-Key": initIdempotencyKey,
      "X-Correlation-Id": correlationId,
    };
    const extToMime: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      webp: "image/webp",
    };
    const fallbackExt =
      image.name.split(".").pop()?.toLowerCase() ?? "jpeg";
    const resolvedContentType =
      image.type || extToMime[fallbackExt] || "image/jpeg";

    const initPayload = {
      patient_id: patientId,
      session_id: session_id ?? null,
      file_name: image.name,
      content_type: resolvedContentType,
      file_size_bytes: image.size,
      is_photo_scan: isPhotoScan,
      client_upload_id: clientUploadId,
    };

    const initResponse = await this.executeWithRetry<UploadInitResponse>(
      () =>
        this.firstFrom(
          this.http.post<UploadInitResponse>(
            `${this.UPLOADS_BASE_URL}/init`,
            initPayload,
            { headers: initHeaders }
          )
        ),
      "upload-init",
      signal
    );
    const uploadSessionId = initResponse.data.upload_session_id;
    this.saveUploadIntent(patientId, image, {
      init_idempotency_key: initIdempotencyKey,
      complete_idempotency_key: completeIdempotencyKey,
      client_upload_id: clientUploadId,
      correlation_id: correlationId,
      upload_session_id: uploadSessionId,
    });

    try {
      let completedParts: { part_number: number; etag: string }[] = [];
      const totalBytes = image.size;
      const uploadedBytesPerPart = new Map<number, number>();
      const emitProgress = () => {
        if (!options?.onProgress) return;
        let loaded = 0;
        uploadedBytesPerPart.forEach((value) => (loaded += value));
        options.onProgress(Math.min(loaded, totalBytes), totalBytes);
      };

      if (initResponse.data.strategy === "single_part") {
        const singlePart = initResponse.data.single_part;
        if (!singlePart) {
          throw new Error("Single part upload data missing from init response");
        }
        await this.putToSignedUrl(
          singlePart.url,
          image,
          singlePart.required_headers,
          "single-part-upload",
          signal,
          (loaded, total) => {
            uploadedBytesPerPart.set(1, loaded);
            options?.onProgress?.(loaded, total || totalBytes);
          }
        );
      } else {
        const multipart = initResponse.data.multipart;
        if (!multipart) {
          throw new Error("Multipart upload data missing from init response");
        }
        const chunks = this.chunkFile(image, multipart.part_size_bytes);

        // Collect URLs for every part we'll actually upload (skip ones the
        // server reports as already completed so we resume in-place).
        const alreadyCompleted = new Set<number>(
          multipart.already_completed_parts || []
        );
        const signedPartMap = new Map<number, string>();
        multipart.signed_parts.forEach((part: { part_number: number; url: string }) =>
          signedPartMap.set(part.part_number, part.url)
        );
        const missing: number[] = [];
        for (let i = 0; i < chunks.length; i += 1) {
          const partNumber = i + 1;
          if (alreadyCompleted.has(partNumber)) continue;
          if (!signedPartMap.has(partNumber)) missing.push(partNumber);
        }
        if (missing.length > 0) {
          const batchResponse = await this.executeWithRetry<UploadPartSignResponse>(
            () =>
              this.firstFrom(
                this.http.post<UploadPartSignResponse>(
                  `${this.UPLOADS_BASE_URL}/${uploadSessionId}/parts/sign`,
                  { part_numbers: missing },
                  { headers: initHeaders }
                )
              ),
            "multipart-sign-batch",
            signal
          );
          batchResponse.data.parts.forEach(
            (part: { part_number: number; url: string }) =>
            signedPartMap.set(part.part_number, part.url)
          );
        }

        const partTasks: (() => Promise<{
          part_number: number;
          etag: string;
        }>)[] = [];
        for (let i = 0; i < chunks.length; i += 1) {
          const partNumber = i + 1;
          if (alreadyCompleted.has(partNumber)) {
            uploadedBytesPerPart.set(partNumber, chunks[i].size);
            continue;
          }
          const chunk = chunks[i];
          const signedUrl = signedPartMap.get(partNumber);
          if (!signedUrl) {
            throw new Error(`Missing signed URL for part ${partNumber}`);
          }
          partTasks.push(async () => {
            const etag = await this.putToSignedUrl(
              signedUrl,
              chunk,
              {},
              `multipart-upload-part-${partNumber}`,
              signal,
              (loaded) => {
                uploadedBytesPerPart.set(partNumber, loaded);
                emitProgress();
              }
            );
            return { part_number: partNumber, etag };
          });
        }

        const uploadedParts = await this.runWithConcurrency(
          partTasks,
          this.MULTIPART_CONCURRENCY,
          signal
        );
        completedParts = uploadedParts.sort(
          (a, b) => a.part_number - b.part_number
        );
        emitProgress();
      }

      const completeHeaders = {
        ...(headers || {}),
        "Idempotency-Key": completeIdempotencyKey,
        "X-Correlation-Id": correlationId,
      };
      const completeResponse = await this.executeWithRetry<ImageUploadSuccessResp>(
        () =>
          this.firstFrom(
            this.http.post<ImageUploadSuccessResp>(
              `${this.UPLOADS_BASE_URL}/complete`,
              {
                upload_session_id: uploadSessionId,
                multipart: {
                  parts: completedParts,
                },
              },
              { headers: completeHeaders }
            )
          ),
        "upload-complete",
        signal
      );
      this.clearUploadIntent(patientId, image);
      return completeResponse.data.image_id;
    } catch (err) {
      // On abort or non-retryable error, tell the server so it can release the
      // multipart upload immediately rather than leaking parts until the
      // sweeper fires. Best-effort; do not mask the original error.
      if (
        uploadSessionId &&
        ((err as { name?: string } | null)?.name === "AbortError" ||
          !this.shouldFallbackFromDirectError(err))
      ) {
        try {
          await this.firstFrom(
            this.http.post(
              `${this.UPLOADS_BASE_URL}/${uploadSessionId}/abort`,
              {},
              { headers: initHeaders }
            )
          );
        } catch (abortErr) {
          Sentry.captureMessage(
            `[Photo Scan] Upload abort call failed: ${stringifyUnknownError(abortErr)}`
          );
        }
        this.clearUploadIntent(patientId, image);
      }
      throw err;
    }
  }

  /**
   * Uploads one image for one patient.
   *
   * @returns an observable of the uploaded image's ID.
   */
  uploadImageForPatient(
    patientId: string,
    image: File,
    isPhotoScan = false,
    session_id?: string,
    headers?: Record<string, string>,
    options?: {
      signal?: AbortSignal;
      onProgress?: (loadedBytes: number, totalBytes: number) => void;
    }
  ): Observable<string> {
    const supportedImageFormats = ["png", "jpg", "jpeg", "webp"];

    // example values for image.type: `image/png`, `image/gif`. Some platforms
    // (mobile cameras, older Electron builds) omit image.type entirely — fall
    // back to the file-name extension before rejecting.
    const format =
      (image.type && image.type.split("/")[1]) ||
      image.name.split(".").pop()?.toLowerCase() ||
      "";

    if (!supportedImageFormats.includes(format)) {
      Sentry.captureMessage(
        `[Photo Scan] Image upload for format '${format}' isn't supported.`
      );
      throw new Error(`Image upload for format '${format}' isn't supported.`);
    }

    if (this.directS3UploadEnabled) {
      return from(
        this.uploadImageViaDirectS3(
          patientId,
          image,
          isPhotoScan,
          session_id,
          headers,
          options
        )
      ).pipe(
        catchError((directUploadError) => {
          // Deterministic validation failures (4xx) will fail identically via
          // the legacy path — don't retry them, just surface the error.
          if (!this.shouldFallbackFromDirectError(directUploadError)) {
            Sentry.captureException(
              directUploadError instanceof Error
                ? directUploadError
                : new Error(stringifyUnknownError(directUploadError)),
              {
                tags: { flow: "direct_s3_upload", fallback: "false" },
                extra: {
                  patientId,
                  isPhotoScan,
                  errorType: (directUploadError as any)?.constructor?.name,
                  errorStatus: (directUploadError as any)?.status,
                },
              }
            );
            return throwError(directUploadError);
          }
          Sentry.captureException(
            directUploadError instanceof Error
              ? directUploadError
              : new Error(stringifyUnknownError(directUploadError)),
            {
              tags: { flow: "direct_s3_upload", fallback: "true" },
              extra: {
                patientId,
                isPhotoScan,
                errorType: (directUploadError as any)?.constructor?.name,
                errorStatus: (directUploadError as any)?.status,
              },
            }
          );
          return this.uploadViaLegacyCompressedBase64(
            patientId,
            image,
            format,
            isPhotoScan,
            session_id,
            headers
          );
        })
      );
    }

    return this.uploadViaLegacyCompressedBase64(
      patientId,
      image,
      format,
      isPhotoScan,
      session_id,
      headers
    );
  }

  private uploadViaLegacyCompressedBase64(
    patientId: string,
    image: File,
    format: string,
    isPhotoScan = false,
    session_id?: string,
    headers?: Record<string, string>
  ): Observable<string> {
    Sentry.captureMessage(
      `[Photo Scan] Compressing image. File name: ${image.name}. Size: ${image.size}`
    );
    return from(
      imageCompression(image, {
        initialQuality: 0.6,
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
      })
    ).pipe(
      catchError((e) => {
        const errorMessage = stringifyUnknownError(e);
        Sentry.captureException(e);
        Sentry.captureMessage(
          `[Photo Scan] Image compression failed. File name: ${image.name}. Original type: ${image.type}. Error: ${errorMessage}`
        );
        throw new Error("Image compression failed");
      }),
      concatMap((compressedImage) => {
        Sentry.captureMessage(
          `[Photo Scan] Compressed image. File name: ${compressedImage.name}. Size: ${compressedImage.size}. Type: ${compressedImage.type}`
        );
        return from(
          this.uploadViaLegacyBase64(
            patientId,
            image,
            compressedImage,
            format,
            isPhotoScan,
            session_id,
            headers
          )
        );
      }),
      catchError((e) => {
        const errorMessage = stringifyUnknownError(e);
        const httpDetails = this.getHttpErrorDetails(e);
        Sentry.captureException(e);
        Sentry.captureMessage(
          `[Photo Scan] Failed to upload photo scan. HTTP: ${httpDetails.isHttpError}. Status: ${httpDetails.status}. Status text: ${httpDetails.statusText || "n/a"}. URL: ${httpDetails.url || this.BASE_URL}. Response message: ${httpDetails.responseMessage || "n/a"}. Error: ${errorMessage}`
        );
        throw new Error(
          `Failed to upload photo scan (status: ${httpDetails.status}, retryable: ${this.isRetryableUploadError(
            e
          )})`
        );
      })
    );
  }

  /**
   * Maps session-indexed image responses from server to the Image model.
   * @param patientImagesResponse Session-indexed image response from server.
   */
  static parseImageResponses(
    patientImagesResponse: SessionIndexed<ImageResponse>
  ): Image[] {
    const imageResponses: ImageResponse[] = ImageService.flattenSessionIndexedEntity(
      patientImagesResponse
    );

    return imageResponses.map(ImageService.parseImageResponse);
  }

  /**
   * Given an image, checks whether a user has confirmed it or not.
   *
   * @param image the image to check confirmation status on.
   *
   * @returns `true` if the image has been confirmed, else `false`.
   */
  static isImageConfirmed(image: Image): boolean {
    if (isNullable(image)) return false;

    return image.status === FindingStatus.Confirmed;
  }

  /**
   * Given images from a session, determine if all of them are confirmed.
   *
   * @param images the images from a session.
   *
   * @returns `true` if all images are confirmed, else `false`.
   */
  static isSessionConfirmed(images: Image[]): boolean {
    if (images.length === 0) return false;

    return images.every((image) => ImageService.isImageConfirmed(image));
  }

  /**
   * Given a session-indexed entity, returns an array of the values without
   * the session-indexed keys.
   *
   * Internally, the function unwraps the `SessionIndexed<T>` wrapper interface
   * and returns the internal `T[]`.
   */
  static flattenSessionIndexedEntity<T extends ImageLike>(
    entity: SessionIndexed<T>
  ): T[] {
    return flatten(Object.values(entity));
  }

  /**
   * Parses a server-responded image the Image model used in the app.
   */
  static parseImageResponse(imageResponse: ImageResponse): Image {
    const sessionId =
      imageResponse.sessionId ?? (imageResponse.session as string);

    return {
      id: imageResponse._id,
      url: imageResponse.url,
      templateId: imageResponse.template_id,
      template_type: imageResponse.template_type,
      xrayCaptureTime: imageResponse.created_datetime,
      orderMode: ImageOrderMode.NotInitialized,
      orderId: !isNullable(imageResponse.order_id)
        ? imageResponse.order_id
        : undefined,
      captureTime: ImageService.parseImageCaptureDate(
        imageResponse.image_datetime
      ),
      xrayType: ClassificationService.classificationResolver(
        imageResponse.xray_image_type
      ),
      classifierModelVersion: imageResponse.classifier_model_ver || undefined,
      treatments: imageResponse.treatments || [],
      findings: imageResponse.findings
        .filter(isFinding)
        .filter(keepUnique)
        .map(FindingAdapter.toFinding),
      imageType: imageResponse.image_type,
      status: imageResponse.status,
      sessionId,
    };
  }

  /**
   * Parses order mode in server response into `ImageOrderMode` enum.
   *
   * @param rawOrderMode order mode in server response
   */
  static imageOrderModeResolver(
    rawOrderMode: string | null | undefined
  ): ImageOrderMode {
    switch (rawOrderMode) {
      case RawImageOrderModes.PREDICTED:
        return ImageOrderMode.Predicted;
      case RawImageOrderModes.USER:
        return ImageOrderMode.User;
      default:
        return ImageOrderMode.NotInitialized;
    }
  }

  /**
   * Sorts a session's image entities.
   *
   * The sorted images returned by this function will have the following order:
   * ```txt
   * [
   *  <optional, bitewing images in template order>,  // not always present
   *  <non-bitewing images in chronological order>    // other images
   * ]
   * ```
   */
  static sortImageSession(
    imageSession: Image[]
  ): (Image & { orderId: number })[] {
    const sortedBitewings = ClassificationService.sortByImageFilter({
      images: imageSession,
      filter: ImageFilters.Bitewings,
    }).map((g, orderId) => ({ ...g, orderId }));

    const nonBitewings = imageSession.filter(
      (image) => !sortedBitewings.some((other) => other.id === image.id)
    );

    const sortedNonBitewings = ImageService.sortImagesByCaptureTime(
      nonBitewings
    ).map((g, index) => ({ ...g, orderId: sortedBitewings.length + index }));

    return [...sortedBitewings, ...sortedNonBitewings];
  }

  /**
   * Given an image array, return an array with the same elements but sorted
   * according to their capture time.
   *
   * The function assumes all images have a valid capture time property.
   * If an array where some images do not have capture time defined is provided,
   * the returned array will still contain all the images, but the ordering
   * of images without capture time is undefined.
   *
   * @param images      images to sort, should all include capture time property.
   * @param descending  optionally specify to sort in reverse-chronological order
   */
  static sortImagesByCaptureTime(images: Image[], descending = false): Image[] {
    type ImageWithCaptureTime = Image & { captureTime: Date };

    const ascendSortVal = (a: ImageWithCaptureTime, b: ImageWithCaptureTime) =>
      a.captureTime.getTime() - b.captureTime.getTime();

    const descendSortVal = (a: ImageWithCaptureTime, b: ImageWithCaptureTime) =>
      b.captureTime.getTime() - a.captureTime.getTime();

    const imageHasCaptureTime = (g: Image): g is ImageWithCaptureTime =>
      g.captureTime !== null || g.captureTime !== undefined;

    return images.sort((a, b) => {
      if (imageHasCaptureTime(a) && imageHasCaptureTime(b)) {
        return descending ? descendSortVal(a, b) : ascendSortVal(a, b);
      }
      return 0;
    });
  }

  /**
   * Given an image capture date string, returns a `Date` object representing
   * that date if the date string is formatted correctly, or null otherwise.
   */
  static parseImageCaptureDate(imageCaptureDate: string): Date | null {
    const isDateValid = (d: Date) => !Number.isNaN(d.getTime());

    const constructedDate = new Date(imageCaptureDate);
    return isDateValid(constructedDate) ? constructedDate : null;
  }

  /**
   * Determines if an image session is unordered.
   *
   * Unordered sessions can proceed to be automatically assigned an order.
   */
  static areImagesUnordered(anyImages: Image[]): boolean {
    return anyImages.every(
      ({ orderId }) => !ImageService.validateImageOrderID(orderId)
    );
  }

  /** Given an order ID of an image, determines whether the order ID is valid. */
  static validateImageOrderID(orderId: number | null | undefined) {
    return (
      // cannot be null or undefined
      !isNullable(orderId) &&
      // must be non-negative
      orderId >= 0
    );
  }

  /**
   * Given an image, determines whether it has run through the prediction API.
   *
   * @param image the image to test whether it has been predicted
   */
  static hasImageBeenPredicted(image: Image): boolean {
    const imageWithoutFindings = (g: Image) => g.findings.length === 0;

    if (isNullable(image)) return false;
    if (isNullable(image.findings)) return false;

    return !imageWithoutFindings(image);
  }

  /**
   * Given an images array, return the images that have not been predicted by
   * the predictions API.
   *
   * @param images the array of images to filter from.
   */
  static filterImagesToPredict(images: Image[]) {
    const imageWithoutFindings = (g: Image) => g.findings.length === 0;
    return images
      .filter((g) => imageWithoutFindings(g))
      .filter(
        (g) => !g.isBeingPredicted && g.status !== FindingStatus.Confirmed
      );
  }
}
